import { Button, HeadlineText, TextField, theme } from "@classdojo/web/nessie";
import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import { IdeasAddIcon } from "components/IdeasIcons";
import PersonListItem, { Person } from "components/PersonListItem";
import RoundedIconButton from "components/RoundedIconButton";
import uniqueId from "lodash/uniqueId";
import { useCallback, useMemo, useState } from "react";
import { logParentToolkitEvent } from "src/utils/logEvent";

type EditPeopleModalProps = {
  innerHeight?: string | number;
  onClose: VoidFunction;
  onComplete: (additionalPersons: Person[], deselectedIds: string[]) => void;
  persons: Person[];
  initialDeselectedIds: string[];
};

export default function EditPeopleModal({
  innerHeight,
  onClose,
  onComplete,
  persons,
  initialDeselectedIds,
}: EditPeopleModalProps) {
  const [pendingAdditionalPersons, setPendingAdditionalPersons] = useState<Person[]>([]);
  const [pendingDeselectedIds, setPendingDeselectedIds] = useState(initialDeselectedIds);
  const [newPerson, setNewPerson] = useState("");

  const selectablePersons = useMemo(
    () => persons.concat(pendingAdditionalPersons),
    [persons, pendingAdditionalPersons],
  );

  const trimmedNewPerson = useMemo(() => newPerson.trim(), [newPerson]);

  const addPerson = useCallback(() => {
    if (!trimmedNewPerson) {
      setNewPerson("");
      return;
    }
    logParentToolkitEvent({ eventName: `whose_turn.edit.add.tap` });
    setPendingAdditionalPersons([
      ...pendingAdditionalPersons,
      {
        _id: uniqueId(trimmedNewPerson),
        firstName: trimmedNewPerson,
        avatar: `//pstatic.classdojo.com/img2/monsters_v5/128/cute_v5_${Math.floor(Math.random() * 20) + 1}.png`,
      },
    ]);
    setNewPerson("");
  }, [setPendingAdditionalPersons, pendingAdditionalPersons, trimmedNewPerson, setNewPerson]);

  const togglePerson = useCallback(
    (id: string) => {
      if (pendingDeselectedIds.includes(id)) {
        setPendingDeselectedIds(pendingDeselectedIds.filter((i) => i !== id));
      } else {
        setPendingDeselectedIds([...pendingDeselectedIds, id]);
      }
    },
    [pendingDeselectedIds, setPendingDeselectedIds],
  );

  return (
    <div
      sx={{
        paddingTop: RAW_cssValue(20),
        height: innerHeight,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <div
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <div sx={{ marginRight: "dt_l", position: "relative" }}>
          <Button data-name="edit_people_modal:toolkit.whose_turn.cancel" kind="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
        <HeadlineText as="h2">People</HeadlineText>
        <div sx={{ marginLeft: "dt_l", position: "relative" }}>
          <Button
            data-name="edit_people_modal:toolkit.whose_turn.done"
            kind="secondary"
            onClick={() => onComplete(pendingAdditionalPersons, pendingDeselectedIds)}
          >
            Done
          </Button>
        </div>
      </div>
      <div sx={{ margin: "dt_s", marginTop: 0, flexGrow: 1, position: "relative" }}>
        {selectablePersons.map((person) => {
          return (
            <PersonListItem
              key={person._id}
              person={person}
              selected={!pendingDeselectedIds.includes(person._id)}
              toggle={togglePerson}
            />
          );
        })}
      </div>
      <div
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "dt_m",
          position: "relative",
        }}
      >
        <div sx={{ marginRight: "dt_s", position: "relative" }}>
          <TextField
            placeholder={autoTranslate("Add person")}
            aria-label={autoTranslate("Add person")}
            onChange={(val) => setNewPerson(val)}
            value={newPerson}
          />
        </div>
        <RoundedIconButton
          backgroundColor={theme.colors.dt_content_accent}
          onClick={addPerson}
          data-name="edit_people_modal:add_button"
        >
          <IdeasAddIcon size="l" color="dt_content_light" />
        </RoundedIconButton>
      </div>
    </div>
  );
}
